import {
  ScrambleTable,
  MatchupCallbackIdentifyingInfo,
  AttemptScrambleInfo,
  MatchupID,
  ResultForTimedAttemptWithPenalty,
} from "@cubing/scramble-table";
import { randomScrambleForEvent } from "cubing/scramble";
import { mountCreateCompetitionModal } from "./create-competition-modal";

interface Entrant {
  id: number;
  name: string;
}

interface Slot {
  id: string;
  entrant: Entrant | null;
}

interface Matchup {
  id: string;
  state: string;
  slots: Slot[];
}

interface MatchupData {
  competitionName: string;
  [key: number]: {
    id: number;
    slots: Slot[];
  };
}

async function resetMatchupCallback() {
  console.log("resetMatchupCallback called");
  // Create the modal if it doesn't already exist
  let modal = document.getElementById("resetMatchupModal") as HTMLElement;
  if (!modal) {
    console.log("creating modal...");
    document.body.insertAdjacentHTML(
      "beforeend",
      `
<div id="resetMatchupModal" class="modal">
  <div class="modal-content">
    <span id="closeResetModal" class="close">&times;</span>
    <h2>Reset Matchup</h2>
    <button id="resetEntireMatchupButton" class="red-button">Reset Entire Last Matchup</button>
    <div style="display: flex; justify-content: space-between; margin-top: 20px; gap: 20px;">
      <div style="flex: 1; text-align: center; margin-right: 10px;">
        <h3 id="competitor1Name">Competitor 1</h3>
        <button id="removeLastSolveCompetitor1" class="red-button">Remove Last Solve</button>
      </div>
      <div style="flex: 1; text-align: center; margin-left: 10px;">
        <h3 id="competitor2Name">Competitor 2</h3>
        <button id="removeLastSolveCompetitor2" class="red-button">Remove Last Solve</button>
      </div>
    </div>
  </div>
</div>
      `,
    );

    modal = document.getElementById("resetMatchupModal") as HTMLElement;
    const closeModalButton = document.getElementById(
      "closeResetModal",
    ) as HTMLElement;
    const resetEntireMatchupButton = document.getElementById(
      "resetEntireMatchupButton",
    ) as HTMLElement;
    const removeLastSolveCompetitor1Button = document.getElementById(
      "removeLastSolveCompetitor1",
    ) as HTMLElement;
    const removeLastSolveCompetitor2Button = document.getElementById(
      "removeLastSolveCompetitor2",
    ) as HTMLElement;

    // Close modal
    closeModalButton.addEventListener("click", () => {
      modal.style.display = "none";
    });

    // Reset entire matchup
    resetEntireMatchupButton.addEventListener("click", async () => {
      const confirmMessages = [
        "Are you sure you want to reset this matchup?",
        "Really? Are you absolutely certain about this?",
        "This will delete ALL progress of this matchup! Still sure?",
        "There's no going back after this! Continue?",
        "Maybe you should think about this some more?",
        "This is a pretty serious decision... Still want to?",
        "Last chance to keep your matchup data! Sure about this?",
        "Wow, you're persistent! Really want to delete everything?",
        "Nine confirmations wasn't enough?! Still going?",
        "Final warning! Are you ABSOLUTELY certain?!",
      ];

      for (let i = 0; i < confirmMessages.length; i++) {
        if (!window.confirm(confirmMessages[i])) {
          return;
        }
      }
      const password = getStoredPassword();
      if (!password) {
        console.error("No password found");
        return;
      }

      if (!app.selectedMatchup) {
        window.alert("Please select a matchup first");
        return;
      }

      const response = await fetch(
        "https://api.timebase.live/psl/clear-round",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            set_id: app.selectedMatchup.id,
            password: password,
          }),
        },
      );

      if (!response.ok) {
        console.error(`Failed to reset matchup: ${response.statusText}`);
        return;
      }

      const result = await response.json();
      if (result.success) {
        window.alert("Matchup reset successfully");
        window.location.reload();
      } else {
        console.error(`Failed to reset matchup: ${result.message}`);
      }
    });

    // Remove last solve for competitor 1
    removeLastSolveCompetitor1Button.addEventListener("click", async () => {
      await removeLastSolve(app.selectedMatchup.slots[0].entrant.id.toString());
    });

    // Remove last solve for competitor 2
    removeLastSolveCompetitor2Button.addEventListener("click", async () => {
      await removeLastSolve(app.selectedMatchup.slots[1].entrant.id.toString());
    });
  }

  // Populate modal with competitor names
  const competitor1Name = document.getElementById(
    "competitor1Name",
  ) as HTMLElement;
  const competitor2Name = document.getElementById(
    "competitor2Name",
  ) as HTMLElement;
  competitor1Name.textContent = app.selectedMatchup.slots[0].entrant.name;
  competitor2Name.textContent = app.selectedMatchup.slots[1].entrant.name;

  // Open modal
  modal.style.display = "block";
}

async function removeLastSolve(competitorId: string) {
  const confirmMessages = [
    "Are you sure you want to remove the last solve?",
    "This action cannot be undone. Continue?",
    "Final confirmation: Remove the last solve?",
  ];

  for (let i = 0; i < confirmMessages.length; i++) {
    if (!window.confirm(confirmMessages[i])) {
      return;
    }
  }

  const password = getStoredPassword();
  if (!password) {
    console.error("No password found");
    return;
  }

  const response = await fetch(
    "https://api.timebase.live/psl/remove-last-result-for-competitor",
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        set_id: app.selectedMatchup.id,
        competitor_id: competitorId,
        password: password,
      }),
    },
  );

  if (!response.ok) {
    console.error(`Failed to remove last solve: ${response.statusText}`);
    return;
  }

  const result = await response.json();
  if (result.success) {
    window.alert("Last solve removed successfully");
    window.location.reload();
  } else {
    console.error(`Failed to remove last solve: ${result.message}`);
  }
}

async function getCurrentScrambleFromServer(
  competitorId1: string,
  competitorId2: string,
) {
  var response = await fetch(
    "https://api.timebase.live/psl/scramble?competitor_id1=" +
      competitorId1 +
      "&competitor_id2=" +
      competitorId2 +
      "&set_id=" +
      app.selectedMatchup.id,
  );

  if (!response.ok) {
    console.error(`Failed to fetch scramble: ${response.statusText}`);
    return {};
  }

  const text = await response.text();
  try {
    const data = JSON.parse(text);
    return data;
  } catch (error) {
    return null;
  }
}

async function appendScramble(
  competitorId: string,
  solveNumber: number,
  setId: string,
  competitionId: string,
  scramble: string,
) {
  const response = await fetch(
    "https://api.timebase.live/psl/append-scramble",
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        competitor_id: competitorId,
        solve_number: solveNumber,
        set_id: setId,
        competition_id: competitionId,
        scramble: scramble,
      }),
    },
  );

  if (!response.ok) {
    console.error(`Failed to append scramble: ${response.statusText}`);
    return;
  }

  const result = await response.text();
  if (result !== "OK") {
    console.error(`Failed to append scramble: ${result}`);
  }
}

async function refreshCurrentMatchupsCallback(competitionId: string) {
  const response = await fetch(
    "https://api.timebase.live/psl/matchups?competition_id=" + competitionId,
  );

  if (!response.ok) {
    console.error(`Failed to fetch matchups: ${response.statusText}`);
    return {};
  }

  const data: MatchupData = await response.json();
  const matchups: { [key: string]: string } = {};

  const activeMatchups = Object.values(data).filter(
    (matchup) =>
      typeof matchup !== "string" &&
      matchup.slots &&
      matchup.slots[0].entrant &&
      matchup.slots[1].entrant &&
      matchup.state !== 3,
  );

  const finishedMatchups = Object.values(data).filter(
    (matchup) =>
      typeof matchup !== "string" &&
      matchup.slots &&
      matchup.slots[0].entrant &&
      matchup.slots[1].entrant &&
      matchup.state === 3,
  );

  activeMatchups.forEach((matchup: Matchup) => {
    const entrant1 = matchup.slots[0].entrant!.name;
    const entrant2 = matchup.slots[1].entrant!.name;
    matchups[`matchup-${matchup.id}`] = `${entrant1} vs. ${entrant2}`;
  });

  if (finishedMatchups.length > 0) {
    matchups["separator-finished"] = "──── Finished Sets ────";
  }

  finishedMatchups.forEach((matchup: Matchup) => {
    const entrant1 = matchup.slots[0].entrant!.name;
    const entrant2 = matchup.slots[1].entrant!.name;
    matchups[`matchup-${matchup.id}`] = `${entrant1} vs. ${entrant2}`;
  });

  app.matchupData = data;
  app.matchups = matchups;
  return matchups;
}

async function getFormatOnServer() {
  const response = await fetch(
    `https://api.timebase.live/psl/get-num-solves?set_id=${app.selectedMatchup.id}`,
  );

  if (!response.ok) {
    console.error(`Failed to get format: ${response.statusText}`);
    return null;
  }

  try {
    const data = await response.json();
    if (Array.isArray(data) && data.length > 0 && data[0].num_solves) {
      return data[0].num_solves;
    }
    return null;
  } catch (error) {
    console.error(`Failed to parse format: ${error}`);
    return null;
  }
}

async function updateFormatOnServer(format: number) {
  const response = await fetch("https://api.timebase.live/psl/update-format", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      competitor_1_id: app.competitorId1,
      competitor_2_id: app.competitorId2,
      set_id: app.selectedMatchup.id,
      format: format,
    }),
  });

  if (!response.ok) {
    console.error(`Failed to update format: ${response.statusText}`);
    return false;
  }

  const result = await response.text();
  return result === "OK";
}

async function matchupFinishAttemptCallback(
  identifyingInfo: MatchupCallbackIdentifyingInfo,
): Promise<void> {
  app.scrambleTable.displays[identifyingInfo.displayNumber].clearScramble();
  var currentScramble = null;
  if (identifyingInfo.displayNumber == 0) {
    currentScramble = await getCurrentScrambleFromServer(
      app.competitorId2,
      app.competitorId1,
    );
  } else {
    currentScramble = await getCurrentScrambleFromServer(
      app.competitorId1,
      app.competitorId2,
    );
    await appendScramble(
      identifyingInfo.displayNumber == 0
        ? app.competitorId2
        : app.competitorId1,
      currentScramble.solve_number,
      app.selectedMatchup.id,
      app.competitionId,
      currentScramble.scramble,
    );
  }
  if (currentScramble == null) {
    var newScramble = (await randomScrambleForEvent("333")).toString();
    currentScramble = { scramble: newScramble, solve_number: 1 };
    await appendScramble(
      identifyingInfo.displayNumber == 0
        ? app.competitorId2
        : app.competitorId1,
      currentScramble.solve_number,
      app.selectedMatchup.id,
      app.competitionId,
      newScramble,
    );
  } else if (currentScramble.scramble == null) {
    var newScramble = (await randomScrambleForEvent("333")).toString();
    currentScramble.scramble = newScramble;
    await appendScramble(
      identifyingInfo.displayNumber == 0
        ? app.competitorId2
        : app.competitorId1,
      currentScramble.solve_number,
      app.selectedMatchup.id,
      app.competitionId,
      newScramble,
    );
  }
  const entrant1Id = app.selectedMatchup.slots[0].entrant.id;
  const entrant2Id = app.selectedMatchup.slots[1].entrant.id;
  app.competitorId1 = entrant1Id.toString();
  app.competitorId2 = entrant2Id.toString();
  app.scrambleTable.displays[
    identifyingInfo.displayNumber == 0 ? 1 : 0
  ].setScramble({
    competitorName:
      identifyingInfo.displayNumber == 0
        ? app.selectedMatchup.slots[1].entrant.name
        : app.selectedMatchup.slots[0].entrant.name,
    eventID: "333",
    matchupID: app.selectedMatchup.id,
    competitorMatchupID:
      identifyingInfo.displayNumber == 0
        ? app.selectedMatchup.slots[1].entrant.name
            .toLowerCase()
            .replace(/\s+/g, "-")
        : app.selectedMatchup.slots[0].entrant.name
            .toLowerCase()
            .replace(/\s+/g, "-"),
    attemptID: currentScramble.solve_number.toString(),
    scrambleString: currentScramble.scramble,
  });
}

async function matchupSelectedCallback(
  matchupId: MatchupID,
  matchups: { [key: string]: string },
  scrambleTable: ScrambleTable,
) {
  const matchup = matchups[matchupId];
  if (!matchup) {
    console.error(`Matchup ${matchupId} not found`);
    return;
  }
  matchupId = matchupId.replace("matchup-", "");
  const [entrant1, entrant2] = matchup.split(" vs. ");
  const matchupData = Object.values(app.matchupData).find(
    (a) => a.id == matchupId,
  );
  app.selectedMatchup = matchupData;
  if (!matchupData) {
    console.error(`Matchup data for ${matchupId} is null`);
    return;
  }

  if (
    !matchupData.slots ||
    !matchupData.slots[0].entrant ||
    !matchupData.slots[1].entrant
  ) {
    console.error(`Invalid matchup data for ${matchupId}`);
    return;
  }

  const entrant1Id = matchupData.slots[0].entrant.id;
  const entrant2Id = matchupData.slots[1].entrant.id;
  app.competitorId1 = entrant1Id.toString();
  app.competitorId2 = entrant2Id.toString();

  const format = await getFormatOnServer();
  if (format !== null) {
    app.formatSelector.value = format.toString();
    const formatInput = document.getElementById(
      "formatInput",
    ) as HTMLInputElement;
    formatInput.value = format.toString();
  }

  const modal = document.getElementById("settingsModal") as HTMLElement;
  modal.style.display = "block";
  var currentScramble1 = await getCurrentScrambleFromServer(
    app.competitorId1,
    app.competitorId2,
  );
  if (currentScramble1 == null) {
    currentScramble1 = {
      scramble: (await randomScrambleForEvent("333")).toString(),
      attempt: 1,
    };
    scrambleTable.displays[0].setScramble({
      competitorName: entrant1,
      eventID: "333",
      matchupID: matchup,
      competitorMatchupID: entrant1.toLowerCase().replace(/\s+/g, "-"),
      attemptID: "1",
      scrambleString: currentScramble1.scramble,
    });
    await appendScramble(
      app.competitorId1,
      1,
      matchupData.id,
      app.competitionId,
      currentScramble1.scramble,
    );
  } else if (currentScramble1.scramble == null) {
    currentScramble1.scramble = (
      await randomScrambleForEvent("333")
    ).toString();
    const solveNumber1 = currentScramble1.solve_number ?? 1;
    scrambleTable.displays[0].setScramble({
      competitorName: entrant1,
      eventID: "333",
      matchupID: matchup,
      competitorMatchupID: entrant1.toLowerCase().replace(/\s+/g, "-"),
      attemptID: solveNumber1.toString(),
      scrambleString: currentScramble1.scramble,
    });
    await appendScramble(
      app.competitorId1,
      solveNumber1.toString(),
      matchupData.id,
      app.competitionId,
      currentScramble1.scramble,
    );
  } else {
    scrambleTable.displays[0].setScramble({
      competitorName: entrant1,
      eventID: "333",
      matchupID: matchup,
      competitorMatchupID: entrant1.toLowerCase().replace(/\s+/g, "-"),
      attemptID: currentScramble1.solve_number.toString(),
      scrambleString: currentScramble1.scramble,
    });
    await appendScramble(
      app.competitorId1,
      currentScramble1.solve_number.toString(),
      matchupData.id,
      app.competitionId,
      currentScramble1.scramble,
    );
  }
  var currentScramble2 = await getCurrentScrambleFromServer(
    app.competitorId2,
    app.competitorId1,
  );
  if (currentScramble2 == null) {
    currentScramble2 = {
      scramble: (await randomScrambleForEvent("333")).toString(),
      attempt: 1,
    };
    scrambleTable.displays[1].setScramble({
      competitorName: entrant2,
      eventID: "333",
      matchupID: matchup,
      competitorMatchupID: entrant2.toLowerCase().replace(/\s+/g, "-"),
      attemptID: "1",
      scrambleString: currentScramble2.scramble,
    });
    await appendScramble(
      app.competitorId2,
      1,
      matchupData.id,
      app.competitionId,
      currentScramble2.scramble,
    );
  } else if (currentScramble2.scramble == null) {
    currentScramble2.scramble = (
      await randomScrambleForEvent("333")
    ).toString();
    const solveNumber2 = currentScramble2.solve_number ?? 1;
    scrambleTable.displays[1].setScramble({
      competitorName: entrant2,
      eventID: "333",
      matchupID: matchup,
      competitorMatchupID: entrant2.toLowerCase().replace(/\s+/g, "-"),
      attemptID: solveNumber2.toString(),
      scrambleString: currentScramble2.scramble,
    });
    await appendScramble(
      app.competitorId2,
      solveNumber2.toString(),
      matchupData.id,
      app.competitionId,
      currentScramble2.scramble,
    );
  } else {
    scrambleTable.displays[1].setScramble({
      competitorName: entrant2,
      eventID: "333",
      matchupID: matchup,
      competitorMatchupID: entrant2.toLowerCase().replace(/\s+/g, "-"),
      attemptID: currentScramble2.solve_number.toString(),
      scrambleString: currentScramble2.scramble,
    });
    await appendScramble(
      app.competitorId2,
      currentScramble2.solve_number.toString(),
      matchupData.id,
      app.competitionId,
      currentScramble2.scramble,
    );
  }
}

async function verifyPassword(password: string): Promise<boolean> {
  const response = await fetch(
    "https://api.timebase.live/psl/verify-password",
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ password }),
    },
  );

  if (!response.ok) {
    console.error(`Failed to verify password: ${response.statusText}`);
    return false;
  }

  try {
    const result = await response.json();
    return result.success === true;
  } catch (error) {
    console.error(`Failed to parse response: ${error}`);
    return false;
  }
}

function getStoredPassword(): string | null {
  return (
    document.cookie
      .split("; ")
      .find((row) => row.startsWith("psl_password="))
      ?.split("=")[1] || null
  );
}

function storePassword(password: string): void {
  const expirationDate = new Date();
  expirationDate.setDate(expirationDate.getDate() + 30);
  document.cookie = `psl_password=${password}; expires=${expirationDate.toUTCString()}; path=/`;
}

async function matchupAdjustPenaltyCallback(
  identifyingInfo: MatchupCallbackIdentifyingInfo,
  deltaSeconds: number,
): Promise<ResultForTimedAttemptWithPenalty> {
  var penalty = 0;
  const password = getStoredPassword();
  if (!password) {
    console.error("No password found");
    return;
  }

  if (identifyingInfo.displayNumber == 0) {
    app.solve1Penalty += deltaSeconds;
    penalty = app.solve1Penalty;
  } else {
    app.solve2Penalty += deltaSeconds;
    penalty = app.solve2Penalty;
  }
  const result = {
    resultForTimedAttempt:
      identifyingInfo.displayNumber == 0 ? app.solve1 : app.solve2,
    penaltySeconds: penalty,
  };

  result.penaltySeconds = penalty;
  if (result.penaltySeconds < 0) {
    result.penaltySeconds = 0;
  }

  const response = await fetch("https://api.timebase.live/psl/modify-penalty", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      competitor_id:
        identifyingInfo.displayNumber == 0
          ? app.competitorId1
          : app.competitorId2,
      set_id: app.selectedMatchup.id,
      penalty: result.penaltySeconds || null,
      password: password,
    }),
  });

  if (!response.ok) {
    console.error(`Failed to update penalty: ${response.statusText}`);
  }

  return structuredClone(result);
}
async function matchupToggleDNF(
  identifyingInfo: MatchupCallbackIdentifyingInfo,
): Promise<ResultForTimedAttemptWithPenalty> {
  var penalty = 0;
  const password = getStoredPassword();
  if (!password) {
    console.error("No password found");
    return;
  }

  if (identifyingInfo.displayNumber == 0) {
    app.solve1Penalty = app.solve1Penalty === -1 ? 0 : -1;
    penalty = app.solve1Penalty;
  } else {
    app.solve2Penalty = app.solve2Penalty === -1 ? 0 : -1;
    penalty = app.solve2Penalty;
  }

  const result = {
    resultForTimedAttempt:
      identifyingInfo.displayNumber == 0 ? app.solve1 : app.solve2,
    penaltySeconds: penalty,
  };

  const response = await fetch("https://api.timebase.live/psl/modify-penalty", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      competitor_id:
        identifyingInfo.displayNumber == 0
          ? app.competitorId1
          : app.competitorId2,
      set_id: app.selectedMatchup.id,
      penalty: result.penaltySeconds,
      password: password,
    }),
  });

  if (!response.ok) {
    console.error(`Failed to update DNF: ${response.statusText}`);
  }

  return structuredClone(result);
}
async function matchupGetResultCallback(
  identifyingInfo: MatchupCallbackIdentifyingInfo,
): Promise<ResultForTimedAttemptWithPenalty> {
  const response = await fetch(
    "https://api.timebase.live/psl/get-latest-result",
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        competitor_id:
          identifyingInfo.displayNumber == 0
            ? app.competitorId1
            : app.competitorId2,
        set_id: app.selectedMatchup.id,
      }),
    },
  );

  if (!response.ok) {
    console.error(`Failed to get latest result: ${response.statusText}`);
    return structuredClone({
      resultForTimedAttempt: 0,
      penaltySeconds: 0,
    });
  }

  const results = await response.json();

  if (!Array.isArray(results) || results.length === 0) {
    return structuredClone({
      resultForTimedAttempt: 0,
      penaltySeconds: 0,
    });
  }

  const latestResult = results[0];
  alert("Latest result: " + latestResult.result);
  const result = {
    resultForTimedAttempt: Number(latestResult.result) * 1000,
    penaltySeconds: Number(latestResult.penalty || 0),
  };

  // Store the values in app state
  if (identifyingInfo.displayNumber == 0) {
    app.solve1 = result.resultForTimedAttempt;
    app.solve1Penalty = result.penaltySeconds;
  } else {
    app.solve2 = result.resultForTimedAttempt;
    app.solve2Penalty = result.penaltySeconds;
  }

  return structuredClone(result);
}
async function showResultsManagementCallback() {
  console.log("showResultsManagementCallback called");

  if (!app.selectedMatchup) {
    window.alert("Please select a matchup first");
    return;
  }

  // Create the modal if it doesn't already exist
  let modal = document.getElementById("resultsManagementModal") as HTMLElement;
  if (!modal) {
    console.log("creating results management modal...");
    document.body.insertAdjacentHTML(
      "beforeend",
      `
<div id="resultsManagementModal" class="modal">
  <div class="modal-content results-modal">
    <span id="closeResultsModal" class="close">&times;</span>
    <h2>Manage Results</h2>
    <div id="resultsContainer" class="results-container">
      <!-- Results will be populated here -->
    </div>
  </div>
</div>
      `,
    );

    // Add styles for the results modal
    const style = document.createElement("style");
    style.textContent = `
      .results-modal {
        width: 80% !important;
        max-height: 80vh;
        overflow-y: auto;
      }
      
      .results-container {
        margin-top: 20px;
      }
      
      .competitor-section {
        margin-bottom: 30px;
        border: 1px solid #ddd;
        border-radius: 8px;
        padding: 15px;
        background: rgba(255, 255, 255, 0.1);
      }
      
      .competitor-name {
        font-size: 18px;
        font-weight: bold;
        margin-bottom: 15px;
        color: #fff;
      }
      
      .game-section {
        margin-bottom: 20px;
        border: 1px solid rgba(255, 255, 255, 0.2);
        border-radius: 6px;
        padding: 10px;
        background: rgba(255, 255, 255, 0.05);
      }
      
      .game-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 10px;
        padding-bottom: 8px;
        border-bottom: 1px solid rgba(255, 255, 255, 0.1);
      }
      
      .game-title {
        font-size: 16px;
        font-weight: bold;
        color: #fff;
      }
      
      .solve-row {
        display: flex;
        align-items: center;
        margin-bottom: 10px;
        padding: 10px;
        background: rgba(255, 255, 255, 0.05);
        border-radius: 5px;
        gap: 10px;
      }
      
      .solve-number {
        font-weight: bold;
        min-width: 80px;
        color: #fff;
      }
      
      .solve-input {
        padding: 5px;
        border: 1px solid #ccc;
        border-radius: 3px;
        min-width: 100px;
      }
      
      .save-solve-btn {
        background: #4caf50;
        color: white;
        border: none;
        padding: 5px 10px;
        border-radius: 3px;
        cursor: pointer;
        font-size: 12px;
      }
      
      .save-solve-btn:hover {
        background: #45a049;
      }
      
      .delete-solve-btn {
        background: #ff4d4d;
        color: white;
        border: none;
        padding: 5px 10px;
        border-radius: 3px;
        cursor: pointer;
        font-size: 12px;
      }
      
      .delete-solve-btn:hover {
        background: #e60000;
      }
      
      .scramble-link-btn {
        background: #ff9800;
        color: white;
        border: none;
        padding: 5px 10px;
        border-radius: 3px;
        cursor: pointer;
        font-size: 12px;
        text-decoration: none;
        display: inline-block;
      }
      
      .scramble-link-btn:hover {
        background: #e65100;
      }
      
      .add-solve-btn {
        background: #2196f3;
        color: white;
        border: none;
        padding: 5px 10px;
        border-radius: 3px;
        cursor: pointer;
        font-size: 12px;
      }
      
      .add-solve-btn:hover {
        background: #1976d2;
      }
      
      .add-game-btn {
        background: #9c27b0;
        color: white;
        border: none;
        padding: 10px 20px;
        border-radius: 5px;
        cursor: pointer;
        font-size: 14px;
        margin-top: 15px;
        width: 100%;
      }
      
      .add-game-btn:hover {
        background: #7b1fa2;
      }
      
      .input-label {
        color: #fff;
        font-size: 12px;
        margin-right: 5px;
      }
    `;
    document.head.appendChild(style);

    modal = document.getElementById("resultsManagementModal") as HTMLElement;
    const closeModalButton = document.getElementById(
      "closeResultsModal",
    ) as HTMLElement;

    // Close modal
    closeModalButton.addEventListener("click", () => {
      modal.style.display = "none";
    });
  }

  // Fetch and populate results
  await populateResultsData();

  // Open modal
  modal.style.display = "block";
}

async function populateResultsData() {
  const password = getStoredPassword();
  if (!password) {
    console.error("No password found");
    return;
  }

  try {
    const response = await fetch(
      `https://api.timebase.live/psl/scramble-table/set-data?set_id=${app.selectedMatchup.id}&password=${password}`,
    );

    if (!response.ok) {
      console.error(`Failed to fetch results data: ${response.statusText}`);
      return;
    }

    const data = await response.json();
    if (!data.success) {
      console.error(`Failed to fetch results data: ${data.message}`);
      return;
    }

    const resultsContainer = document.getElementById(
      "resultsContainer",
    ) as HTMLElement;
    resultsContainer.innerHTML = "";

    // Group solves by competitor and then by game
    const solvesByCompetitorAndGame = {
      [data.match.competitor_1.id]: {},
      [data.match.competitor_2.id]: {},
    };

    data.solves.forEach((solve: any) => {
      if (solvesByCompetitorAndGame[solve.competitor_id]) {
        if (!solvesByCompetitorAndGame[solve.competitor_id][solve.game]) {
          solvesByCompetitorAndGame[solve.competitor_id][solve.game] = [];
        }
        solvesByCompetitorAndGame[solve.competitor_id][solve.game].push(solve);
      }
    });

    // Create sections for each competitor
    [data.match.competitor_1, data.match.competitor_2].forEach((competitor) => {
      const competitorSection = document.createElement("div");
      competitorSection.className = "competitor-section";
      competitorSection.innerHTML = `
        <div class="competitor-name">${competitor.name}</div>
      `;

      const competitorGames = solvesByCompetitorAndGame[competitor.id] || {};
      const gameNumbers = Object.keys(competitorGames).sort(
        (a, b) => parseInt(a) - parseInt(b),
      );

      gameNumbers.forEach((gameNumber) => {
        const gameSolves = competitorGames[gameNumber];

        const gameSection = document.createElement("div");
        gameSection.className = "game-section";
        gameSection.innerHTML = `
          <div class="game-header">
            <span class="game-title">Game ${gameNumber}</span>
            <button class="add-solve-btn" onclick="addSolve(${competitor.id}, ${gameNumber})">Add Solve</button>
          </div>
        `;

        gameSolves.forEach((solve) => {
          const solveRow = document.createElement("div");
          solveRow.className = "solve-row";

          const displayResult = formatResultForDisplay(
            solve.result,
            solve.penalty,
          );
          const displayPenalty = formatPenaltyForDisplay(solve.penalty);

          solveRow.innerHTML = `
            <div class="solve-number">Solve ${solve.solve_number}</div>
            <div>
              <span class="input-label">Time:</span>
              <input type="text" class="solve-input" id="result-${solve.id}" value="${displayResult}" placeholder="Time (e.g., 12.34, DNF, DNS)">
            </div>
            <div>
              <span class="input-label">Penalty:</span>
              <input type="text" class="solve-input" id="penalty-${solve.id}" value="${displayPenalty}" placeholder="Penalty (0, 2, 4, DNF, DNS)">
            </div>
            <div>
              <span class="input-label">YouTube URL:</span>
              <input type="text" class="solve-input" id="youtube-${solve.id}" value="${solve.youtube_url || ""}" placeholder="YouTube URL">
            </div>
            ${solve.alg_cubing_net_link ? `<a href="${solve.alg_cubing_net_link}" target="_blank" rel="noopener noreferrer" class="scramble-link-btn">Scramble</a>` : ""}
            <button class="save-solve-btn" onclick="saveSolveChanges(${solve.id})">Save</button>
            <button class="delete-solve-btn" onclick="deleteSolve(${solve.id})">Delete</button>
          `;

          gameSection.appendChild(solveRow);
        });

        competitorSection.appendChild(gameSection);
      });

      // Add button to add new game for this competitor
      const addGameButton = document.createElement("button");
      addGameButton.className = "add-game-btn";
      addGameButton.textContent = "Add New Game";
      addGameButton.onclick = () => addGame();
      competitorSection.appendChild(addGameButton);

      resultsContainer.appendChild(competitorSection);
    });
  } catch (error) {
    console.error("Error fetching results data:", error);
  }
}

function formatResultForDisplay(result: number, penalty: number): string {
  if (result === null || result === 0) return "";
  return result.toFixed(3);
}

function formatPenaltyForDisplay(penalty: number): string {
  if (penalty === -1) return "DNF";
  if (penalty === -2) return "DNS";
  if (penalty === 0 || penalty === null) return "0";
  return penalty.toString();
}

function parseResultInput(input: string): {
  result: number | null;
  penalty: number;
} {
  const trimmed = input.trim().toUpperCase();

  if (trimmed === "DNF") {
    return { result: null, penalty: -1 };
  }
  if (trimmed === "DNS") {
    return { result: null, penalty: -2 };
  }
  if (trimmed === "") {
    return { result: null, penalty: 0 };
  }

  const timeValue = parseFloat(trimmed);
  if (isNaN(timeValue)) {
    throw new Error("Invalid time format");
  }

  return { result: timeValue, penalty: 0 };
}

function parsePenaltyInput(input: string): number {
  const trimmed = input.trim().toUpperCase();

  if (trimmed === "DNF") return -1;
  if (trimmed === "DNS") return -2;
  if (trimmed === "" || trimmed === "0") return 0;

  const penaltyValue = parseInt(trimmed);
  if (isNaN(penaltyValue)) {
    throw new Error("Invalid penalty format");
  }

  return penaltyValue;
}

async function saveSolveChanges(solveId: number) {
  const password = getStoredPassword();
  if (!password) {
    console.error("No password found");
    return;
  }

  try {
    const resultInput = (
      document.getElementById(`result-${solveId}`) as HTMLInputElement
    ).value;
    const penaltyInput = (
      document.getElementById(`penalty-${solveId}`) as HTMLInputElement
    ).value;
    const youtubeInput = (
      document.getElementById(`youtube-${solveId}`) as HTMLInputElement
    ).value;

    let result: number | null = null;
    let penalty: number = 0;

    if (penaltyInput.trim().toUpperCase() === "DNF") {
      penalty = -1;
      result = null;
    } else if (penaltyInput.trim().toUpperCase() === "DNS") {
      penalty = -2;
      result = null;
    } else {
      const parsedResult = parseResultInput(resultInput);
      result = parsedResult.result;
      penalty = parsedResult.penalty;

      if (penaltyInput.trim() !== "") {
        penalty = parsePenaltyInput(penaltyInput);
      }
    }

    const response = await fetch(
      "https://api.timebase.live/psl/scramble-table/update-solve",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          solve_id: solveId,
          result: result,
          penalty: penalty,
          youtube_url: youtubeInput || null,
          password: password,
        }),
      },
    );

    if (!response.ok) {
      console.error(`Failed to update solve: ${response.statusText}`);
      window.alert("Failed to update solve");
      return;
    }

    const responseData = await response.json();
    if (responseData.success) {
      window.alert("Solve updated successfully");
      await populateResultsData();
    } else {
      window.alert(`Failed to update solve: ${responseData.message}`);
    }
  } catch (error) {
    console.error("Error updating solve:", error);
    window.alert("Error updating solve: " + error.message);
  }
}

async function deleteSolve(solveId: number) {
  const confirmMessages = [
    "Are you sure you want to delete this solve?",
    "This action cannot be undone. Are you absolutely certain?",
    "Deleting solves is very serious! Still want to continue?",
    "This will permanently remove the solve from the database. Continue?",
    "Last chance! Are you really sure you want to delete this solve?",
  ];

  for (let i = 0; i < confirmMessages.length; i++) {
    if (!window.confirm(confirmMessages[i])) {
      return;
    }
  }

  const password = getStoredPassword();
  if (!password) {
    console.error("No password found");
    return;
  }

  try {
    const response = await fetch(
      "https://api.timebase.live/psl/scramble-table/delete-solve",
      {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          solve_id: solveId,
          password: password,
        }),
      },
    );

    if (!response.ok) {
      console.error(`Failed to delete solve: ${response.statusText}`);
      window.alert("Failed to delete solve");
      return;
    }

    const responseData = await response.json();
    if (responseData.success) {
      window.alert("Solve deleted successfully");
      await populateResultsData();
    } else {
      window.alert(`Failed to delete solve: ${responseData.message}`);
    }
  } catch (error) {
    console.error("Error deleting solve:", error);
    window.alert("Error deleting solve: " + error.message);
  }
}

async function addSolve(competitorId: number, gameNumber: number) {
  const password = getStoredPassword();
  if (!password) {
    console.error("No password found");
    return;
  }

  try {
    const response = await fetch(
      "https://api.timebase.live/psl/scramble-table/create-solve",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          set_id: app.selectedMatchup.id,
          competitor_id: competitorId,
          game: gameNumber,
          password: password,
        }),
      },
    );

    if (!response.ok) {
      console.error(`Failed to add solve: ${response.statusText}`);
      window.alert("Failed to add solve");
      return;
    }

    const responseData = await response.json();
    if (responseData.success) {
      window.alert("Solve added successfully");
      await populateResultsData();
    } else {
      window.alert(`Failed to add solve: ${responseData.message}`);
    }
  } catch (error) {
    console.error("Error adding solve:", error);
    window.alert("Error adding solve: " + error.message);
  }
}

async function addGame() {
  const password = getStoredPassword();
  if (!password) {
    console.error("No password found");
    return;
  }

  try {
    const response = await fetch(
      "https://api.timebase.live/psl/scramble-table/add-game",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          set_id: app.selectedMatchup.id,
          password: password,
        }),
      },
    );

    if (!response.ok) {
      console.error(`Failed to add game: ${response.statusText}`);
      window.alert("Failed to add game");
      return;
    }

    const responseData = await response.json();
    if (responseData.success) {
      window.alert(`Game ${responseData.game_number} added successfully`);
      await populateResultsData();
    } else {
      window.alert(`Failed to add game: ${responseData.message}`);
    }
  } catch (error) {
    console.error("Error adding game:", error);
    window.alert("Error adding game: " + error.message);
  }
}

(globalThis as any).saveSolveChanges = saveSolveChanges;
(globalThis as any).deleteSolve = deleteSolve;
(globalThis as any).addSolve = addSolve;
(globalThis as any).addGame = addGame;

class App {
  tagFunctionPromiseResolver: ((value: string) => void) | null = null;

  competitionId: string | null = null;
  scrambleTable: ScrambleTable;
  competitorId1: string | null = null;
  competitorId2: string | null = null;
  matchups: { [key: string]: string } = {};
  matchupData: MatchupData = null;
  selectedMatchup: Matchup = null;
  formatSelector: HTMLSelectElement;
  solve1: number = 0;
  solve2: number = 0;
  solve1Penalty: number = 0;
  solve2Penalty: number = 0;

  constructor() {
    // Resolve competition ID from URL early; show landing page if not present
    const currentUrl = window.location.href;
    const url = new URL(currentUrl);
    this.competitionId = url.searchParams.get("competitionId");

    if (!this.competitionId) {
      this.showCompetitionLandingPage();
      return;
    }

    document.body.insertAdjacentHTML(
      "beforeend",
      `
<div id="settingsModal" class="modal">
  <div class="modal-content">
    <span id="closeModal" class="close">&times;</span>
    <h2>Matchup Settings</h2>
    <label for="formatInput">Format (1-3):</label>
    <input type="number" id="formatInput" min="1" max="3" />
    <br />
    <button id="saveSettingsButton">Save</button>
  </div>
</div>
<button id="openSettingsButton">Matchup Settings</button>
<button id="openResultsButton">Manage Results</button>
    `,
    );

    const style = document.createElement("style");
    style.textContent = `
      /* Modal Background */
      .modal {
        display: none;
        position: fixed;
        z-index: 1000;
        left: 0;
        top: 0;
        width: 100%;
        height: 100%;
        overflow: auto;
        background: rgba(0, 0, 0, 0.8); /* Darker background with transparency */
        backdrop-filter: blur(5px); /* Add a blur effect */
      }
    
      /* Modal Content */
      .modal-content {
        background: linear-gradient(135deg, #1e3c72, #2a5298); /* Gradient background */
        color: #fff; /* White text */
        margin: 10% auto;
        padding: 20px;
        border-radius: 15px; /* Rounded corners */
        box-shadow: 0 8px 16px rgba(0, 0, 0, 0.3); /* Subtle shadow */
        width: 40%; /* Adjust width */
        text-align: center;
        font-family: 'Arial', sans-serif;
      }
    
      /* Close Button */
      .close {
        color: #fff;
        float: right;
        font-size: 28px;
        font-weight: bold;
        cursor: pointer;
        transition: color 0.3s ease;
      }
    
      .close:hover,
      .close:focus {
        color: #ff6b6b; /* Red hover effect */
        text-decoration: none;
      }
    
      /* Input Fields */
      .modal-content input[type="number"],
      .modal-content input[type="checkbox"] {
        margin: 10px 0;
        padding: 10px;
        border: none;
        border-radius: 5px;
        font-size: 16px;
        width: 80%;
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
      }
    
      /* Save Button */
      .modal-content button {
        background: #4caf50; /* Green button */
        color: white;
        border: none;
        padding: 10px 20px;
        border-radius: 5px;
        font-size: 16px;
        cursor: pointer;
        transition: background 0.3s ease;
      }
    
      .modal-content button:hover {
        background: #45a049; /* Darker green on hover */
      }
    
      /* Open Modal Button */
      #openSettingsButton {
        background: #007bff; /* Blue button */
        color: white;
        border: none;
        padding: 10px 20px;
        border-radius: 5px;
        font-size: 16px;
        cursor: pointer;
        transition: background 0.3s ease;
        margin: 20px;
      }
    
      #openSettingsButton:hover {
        background: #0056b3; /* Darker blue on hover */
      }

      .red-button {
        background: #ff4d4d !important; /* Red button */
        color: white !important;
        border: none !important;
        padding: 10px 20px !important;
        border-radius: 5px !important;
        font-size: 16px !important;
        cursor: pointer !important;
        transition: background 0.3s ease !important;
      }

      .red-button:hover {
        background: #e60000 !important; /* Darker red on hover */
      }
    `;
    document.head.appendChild(style);
    const modal = document.getElementById("settingsModal") as HTMLElement;
    const openModalButton = document.getElementById(
      "openSettingsButton",
    ) as HTMLElement;
    const closeModalButton = document.getElementById(
      "closeModal",
    ) as HTMLElement;
    const saveSettingsButton = document.getElementById(
      "saveSettingsButton",
    ) as HTMLElement;
    const formatInput = document.getElementById(
      "formatInput",
    ) as HTMLInputElement;

    // Open modal
    openModalButton.addEventListener("click", () => {
      formatInput.value = app.formatSelector.value; // Set current format
      modal.style.display = "block";
    });

    // Close modal
    closeModalButton.addEventListener("click", () => {
      modal.style.display = "none";
    });

    // Save settings
    saveSettingsButton.addEventListener("click", async () => {
      const format = parseInt(formatInput.value, 10);
      if (isNaN(format) || format < 1 || format > 3) {
        alert("Format must be an integer between 1 and 3.");
        return;
      }

      // Update format on server
      const formatUpdated = await updateFormatOnServer(format);
      if (!formatUpdated) {
        alert("Failed to update format.");
        return;
      }

      // Update app state
      app.formatSelector.value = format.toString();

      alert("Settings updated successfully.");
      modal.style.display = "none";
    });

    // Close modal when clicking outside of it
    window.addEventListener("click", (event) => {
      if (event.target === modal) {
        modal.style.display = "none";
      }
    });

    const openResultsButton = document.getElementById(
      "openResultsButton",
    ) as HTMLElement;
    openResultsButton.addEventListener("click", () => {
      showResultsManagementCallback();
    });
    this.scrambleTable = document.body.appendChild(
      new ScrambleTable({
        competitorScrambleDisplayOptions: {
          resultInput: "show",
        },
        showMatchupsSelection: "show",
        callbacks: {
          matchupGetResultCallback,
          matchupToggleDNF,
          matchupAdjustPenaltyCallback,
          matchupFinishAttemptCallback,
          refreshCurrentMatchupsCallback: async () => {
            const matchups = await refreshCurrentMatchupsCallback(
              this.competitionId,
            );
            return matchups;
          },
          resetCallback: async () => {
            resetMatchupCallback();
          },
          resultsCallback: async () => {
            showResultsManagementCallback();
          },
          matchupSelectedCallback: async (matchupID: MatchupID) => {
            await matchupSelectedCallback(
              matchupID,
              this.matchups,
              this.scrambleTable,
            );
          },
        },
      }),
    );

    this.formatSelector = document.createElement("select");
    this.formatSelector.id = "formatSelector";
    const option1 = document.createElement("option");
    option1.value = "1";
    option1.text = "First to 1";

    const option2 = document.createElement("option");
    option2.value = "2";
    option2.text = "First to 2";

    const option3 = document.createElement("option");
    option3.value = "3";
    option3.text = "First to 3";

    this.formatSelector.appendChild(option1);
    this.formatSelector.appendChild(option2);
    this.formatSelector.appendChild(option3);

    const optionNo = document.createElement("option");
    optionNo.value = "false";

    const optionYes = document.createElement("option");
    optionYes.value = "true";

    this.formatSelector.addEventListener("change", async (e) => {
      if (!this.competitorId1 || !this.competitorId2) {
        alert("Please select a matchup first");
        return;
      }
      const format = parseInt((e.target as HTMLSelectElement).value);
      const success = await updateFormatOnServer(format);
      if (!success) {
        alert("Failed to update format");
      } else {
        alert("Format updated successfully");
      }
    });

    document.body.insertBefore(this.formatSelector, document.body.firstChild);
    this.initializeApp();
  }

  private async showCompetitionLandingPage() {
    document.title = "TimeBase Scrambler – Select Competition";

    const style = document.createElement("style");
    style.textContent = `
      * { box-sizing: border-box; margin: 0; padding: 0; }
      body {
        background: linear-gradient(135deg, #0f0c29, #302b63, #24243e);
        min-height: 100vh;
        display: flex;
        justify-content: center;
        align-items: flex-start;
        padding: 60px 20px;
        font-family: Arial, sans-serif;
        color: #fff;
      }
      .landing-container {
        max-width: 860px;
        width: 100%;
        text-align: center;
      }
      .landing-title {
        font-size: 40px;
        font-weight: bold;
        margin-bottom: 12px;
        letter-spacing: 1px;
      }
      .landing-subtitle {
        font-size: 18px;
        color: #aab;
        margin-bottom: 48px;
      }
      .competition-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
        gap: 18px;
      }
      .competition-card {
        background: linear-gradient(135deg, #1e3c72, #2a5298);
        border-radius: 14px;
        padding: 24px 18px;
        cursor: pointer;
        text-decoration: none;
        color: #fff;
        transition: transform 0.2s ease, box-shadow 0.2s ease;
        box-shadow: 0 4px 14px rgba(0,0,0,0.35);
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 8px;
      }
      .competition-card:hover {
        transform: translateY(-5px);
        box-shadow: 0 10px 28px rgba(0,0,0,0.55);
      }
      .competition-card-name {
        font-size: 17px;
        font-weight: bold;
        line-height: 1.3;
      }
      .competition-card-id {
        font-size: 12px;
        color: #aac;
      }
      .landing-status {
        font-size: 18px;
        color: #aab;
        margin-top: 30px;
      }
      .landing-error {
        font-size: 18px;
        color: #ff6b6b;
        margin-top: 30px;
      }
    `;
    document.head.appendChild(style);

    document.body.innerHTML = `
      <div class="landing-container">
        <div class="landing-title">TimeBase Scrambler</div>
        <div class="landing-subtitle">Select a competition to continue</div>
        <button id="createCompetitionBtn" class="create-competition-btn">+ Create Competition</button>
        <div id="landing-competitions"><div class="landing-status">Loading competitions…</div></div>
      </div>
    `;

    const container = document.getElementById("landing-competitions")!;

    mountCreateCompetitionModal({
      getPassword: getStoredPassword,
      onCreated: async () => {
        container.innerHTML = `<div class="landing-status">Loading competitions…</div>`;
        await this.loadCompetitionList(container);
      },
    });

    await this.loadCompetitionList(container);
  }

  private async loadCompetitionList(container: HTMLElement) {
    try {
      const response = await fetch(
        "https://api.timebase.live/psl/competition-list",
      );
      if (!response.ok)
        throw new Error(
          `Server responded ${response.status} ${response.statusText}`,
        );

      const raw = await response.json();
      const competitions: {
        id: number | string;
        name?: string;
        [key: string]: unknown;
      }[] = Array.isArray(raw)
        ? raw
        : raw && typeof raw === "object"
          ? [raw]
          : [];

      if (competitions.length === 0) {
        container.innerHTML = `<div class="landing-status">No competitions found.</div>`;
        return;
      }

      const grid = document.createElement("div");
      grid.className = "competition-grid";

      for (const comp of competitions) {
        const card = document.createElement("a");
        card.className = "competition-card";
        card.href = `?competitionId=${encodeURIComponent(comp.id)}`;
        card.innerHTML = `
          <div class="competition-card-name">${comp.name ?? `Competition ${comp.id}`}</div>
          <div class="competition-card-id">ID: ${comp.id}</div>
        `;
        grid.appendChild(card);
      }

      container.innerHTML = "";
      container.appendChild(grid);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      container.innerHTML = `<div class="landing-error">Failed to load competitions: ${msg}</div>`;
    }
  }

  private async initializeApp() {
    // Check for stored password
    const storedPassword = getStoredPassword();
    let isAuthenticated = false;

    if (storedPassword) {
      isAuthenticated = await verifyPassword(storedPassword);
      if (!isAuthenticated) {
        document.cookie =
          "psl_password=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
      }
    }

    if (!isAuthenticated) {
      const password = window.prompt("Please enter the password:");
      if (!password) {
        alert("Password is required to use this application.");
        window.location.reload();
        return;
      }

      isAuthenticated = await verifyPassword(password);
      if (!isAuthenticated) {
        alert("Incorrect password.");
        window.location.reload();
        return;
      }

      storePassword(password);
    }
  }

  async promptForNumberOfCubesAsync(): Promise<number | null> {
    const userInput = window.prompt("How many cubes will they be attempting?");

    if (userInput === null || userInput.trim() === "") {
      return null;
    }

    const numberOfCubes = parseInt(userInput, 10);

    if (isNaN(numberOfCubes)) {
      return null;
    }

    return numberOfCubes;
  }

  async signOut() {
    document.cookie.split(";").forEach(function (c) {
      document.cookie = c
        .replace(/^ +/, "")
        .replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
    });
    window.location.reload();
  }
}

let app = new App();
// Make the app object available in the console for debugging.
// Try running: app.updateScramble()
globalThis.app = app;
